import { fetchWithAuth } from '@/services/api/apiConfig';
import { Location } from '@/services/api/clients/locations';
import { MfaChannel } from '@/services/api/clients/mfa';

export interface WorkType {
  id: number;
  name: string;
  icon: string;
}

export type UserMfaStatus = 'on' | 'reset_pending' | 'not_enrolled';

export interface UserMfaMethod {
  channel: MfaChannel;
  is_primary: boolean;
}

export interface CreateUserPayload {
  email: string;
  role: string;
  locations: string[];
}

export interface AvailableUser {
  uuid: string;
  username: string;
  email: string;
  name: string;
  permission_level: string | null;
  last_login: string | null;
  synced: boolean;
  profile_picture_url: string | null;
  locations: { id: string; name: string; permission_level: string }[];
  // Optional until the MFA backend ships; absent means "not enrolled".
  mfa_status?: UserMfaStatus;
  mfa_methods?: UserMfaMethod[];
}

export interface User {
  uuid: string;
  email: string;
  permission_level?: string; // Org level
  last_login?: string;
  synced?: boolean;
  name?: string;
  profile_picture_url?: string;
  locations?: Location[]; // Contains loc level perms
  mfa_status?: UserMfaStatus;
  /** Enrolled factors, primary first. Empty unless `mfa_status` is `'on'`. */
  mfa_methods?: UserMfaMethod[];
}

/**
 * User client (account + profile + legal acknowledgements).
 *
 * Note: Keep types light for now; we’ll revisit during the types pass.
 */
export const userClient = {
  getUserData: async () => {
    return fetchWithAuth('user/data/');
  },

  updateUserProfile: async (
    userUuid: string,
    first_name?: string,
    last_name?: string,
    profile_picture_url?: string | null,
    job_title?: string,
    phone?: string
  ) => {
    const payload: Record<string, string | null | boolean | undefined> = {};
    if (first_name !== undefined) payload.first_name = first_name;
    if (last_name !== undefined) payload.last_name = last_name;
    if (profile_picture_url !== undefined) payload.profile_picture_url = profile_picture_url;
    if (job_title !== undefined) payload.job_title = job_title;
    if (phone !== undefined) payload.phone = phone;

    return fetchWithAuth(`user/profile/${userUuid}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  uploadProfileImage: async (profile_image: File) => {
    const formData = new FormData();
    formData.append('profile_picture', profile_image);

    return fetchWithAuth(`user/profile/upload-picture/`, {
      method: 'PUT',
      body: formData,
    });
  },

  changePassword: async (oldPassword: string, newPassword: string) => {
    return fetchWithAuth('user/change-password/', {
      method: 'PUT',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    });
  },

  deleteUser: async (userId: string) => {
    return fetchWithAuth(`user/list/${userId}`, {
      method: 'DELETE',
    });
  },

  getAllUsers: async (): Promise<(AvailableUser & { organization_uuids: string[] })[]> => {
    const response = await fetchWithAuth('user/list/');
    return response.data ?? [];
  },

  getUserListForOrganization: async (uuid: string) => {
    const response = await fetchWithAuth(`user/list/?organization_uuid=${uuid}`);
    return response.data || [];
  },

  addUserToOrganization: async (orgUuid: string, payload: CreateUserPayload) => {
    const response = await fetchWithAuth('user/list/', {
      method: 'POST',
      body: JSON.stringify({ ...payload, organization_uuid: orgUuid }),
    });
    return response.data || {};
  },

  updateUserInOrganization: async (
    orgUuid: string,
    user: { uuid: string; role?: string; locations?: { id: string }[] }
  ) => {
    const payload: {
      organization_uuid: string;
      user_id: string;
      role?: string;
      locations?: string[];
    } = {
      organization_uuid: orgUuid,
      user_id: user.uuid,
      ...(user.role && { role: user.role.toLowerCase() }),
      ...(user.locations && { locations: user.locations.map((loc) => loc.id) }),
    };
    const response = await fetchWithAuth('user/list/', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return response.data || {};
  },

  getAvailableUsers: async (
    organizationUuid: string,
    locationUuid?: string | null
  ): Promise<AvailableUser[]> => {
    const params = new URLSearchParams({ organization_uuid: organizationUuid });
    if (locationUuid) params.set('location_uuid', locationUuid);
    const response = await fetchWithAuth(`user/list/?${params}`);
    return response.data ?? [];
  },

  // Legal
  getToS: async () => {
    const response = await fetchWithAuth('user/tos/');
    return response.data;
  },

  acceptToS: async (version: string) => {
    return fetchWithAuth('user/tos/', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  },

  getPrivacyPolicy: async () => {
    const response = await fetchWithAuth('user/privacy-policy/');
    return response.data;
  },

  acceptPrivacyPolicy: async (version: string) => {
    return fetchWithAuth('user/privacy-policy/', {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  },
};
